import * as React from "react"
import { ChevronDown, ChevronsUpDown, ChevronUp } from "lucide-react"
import { cn } from "../lib/utils"
import { Checkbox } from "./Checkbox"
import { Skeleton } from "./Skeleton"

export interface Column<T> {
  id: string
  header: React.ReactNode
  cell: (row: T) => React.ReactNode
  sortable?: boolean
  /** Value used for client-side sorting. */
  sortAccessor?: (row: T) => string | number | null | undefined
  align?: "left" | "center" | "right"
  width?: string
  className?: string
  headerClassName?: string
}

export interface DataTableProps<T> {
  columns: Column<T>[]
  data: T[]
  keyExtractor: (row: T) => string
  isLoading?: boolean
  skeletonRows?: number
  onRowClick?: (row: T) => void
  selectable?: boolean
  selectedKeys?: Set<string>
  onSelectionChange?: (keys: Set<string>) => void
  /** Rendered when there are zero rows (and not loading). */
  empty?: React.ReactNode
  /** Toolbar rendered above the table (search, filters, bulk actions). */
  toolbar?: React.ReactNode
  /** Pagination (or any footer) rendered below the table. */
  footer?: React.ReactNode
  stickyHeader?: boolean
  className?: string
}

type SortState = { id: string; dir: "asc" | "desc" } | null

const alignClass = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
}

export function DataTable<T>({
  columns,
  data,
  keyExtractor,
  isLoading,
  skeletonRows = 8,
  onRowClick,
  selectable,
  selectedKeys,
  onSelectionChange,
  empty,
  toolbar,
  footer,
  stickyHeader = true,
  className,
}: DataTableProps<T>) {
  const [sort, setSort] = React.useState<SortState>(null)

  const sortedData = React.useMemo(() => {
    if (!sort) return data
    const col = columns.find((c) => c.id === sort.id)
    if (!col?.sortAccessor) return data
    const accessor = col.sortAccessor
    return [...data].sort((a, b) => {
      const av = accessor(a)
      const bv = accessor(b)
      if (av == null) return 1
      if (bv == null) return -1
      if (av < bv) return sort.dir === "asc" ? -1 : 1
      if (av > bv) return sort.dir === "asc" ? 1 : -1
      return 0
    })
  }, [data, sort, columns])

  const toggleSort = (id: string) => {
    setSort((prev) => {
      if (prev?.id !== id) return { id, dir: "asc" }
      if (prev.dir === "asc") return { id, dir: "desc" }
      return null
    })
  }

  const allKeys = React.useMemo(
    () => sortedData.map(keyExtractor),
    [sortedData, keyExtractor]
  )
  const selected = selectedKeys ?? new Set<string>()
  const allSelected = allKeys.length > 0 && allKeys.every((k) => selected.has(k))
  const someSelected = allKeys.some((k) => selected.has(k))

  const toggleAll = () => {
    if (!onSelectionChange) return
    onSelectionChange(allSelected ? new Set() : new Set(allKeys))
  }
  const toggleOne = (key: string) => {
    if (!onSelectionChange) return
    const next = new Set(selected)
    next.has(key) ? next.delete(key) : next.add(key)
    onSelectionChange(next)
  }

  const colSpan = columns.length + (selectable ? 1 : 0)

  return (
    <div
      className={cn(
        "overflow-hidden rounded-lg border border-border bg-card shadow-none",
        className
      )}
    >
      {toolbar && (
        <div className="border-b border-border px-4 py-3 bg-card">{toolbar}</div>
      )}
      <div className="scrollbar-thin overflow-x-auto">
        <table className="w-full border-collapse text-xs">
          <thead
            className={cn(
              "bg-surface-2",
              stickyHeader && "sticky top-0 z-10"
            )}
          >
            <tr className="border-b border-border">
              {selectable && (
                <th className="w-10 px-4">
                  <Checkbox
                    checked={
                      allSelected
                        ? true
                        : someSelected
                        ? "indeterminate"
                        : false
                    }
                    onCheckedChange={toggleAll}
                    aria-label="Select all rows"
                  />
                </th>
              )}
              {columns.map((col) => {
                const isSorted = sort?.id === col.id
                return (
                  <th
                    key={col.id}
                    style={{ width: col.width }}
                    className={cn(
                      "h-9 px-4 text-xs font-medium text-muted-foreground",
                      alignClass[col.align ?? "left"],
                      col.headerClassName
                    )}
                  >
                    {col.sortable ? (
                      <button
                        type="button"
                        onClick={() => toggleSort(col.id)}
                        className={cn(
                          "inline-flex items-center gap-1.5 transition-colors hover:text-foreground cursor-pointer",
                          isSorted && "text-foreground",
                          col.align === "right" && "flex-row-reverse"
                        )}
                      >
                        {col.header}
                        {isSorted ? (
                          sort!.dir === "asc" ? (
                            <ChevronUp className="size-3.5" />
                          ) : (
                            <ChevronDown className="size-3.5" />
                          )
                        ) : (
                          <ChevronsUpDown className="size-3.5 opacity-40" />
                        )}
                      </button>
                    ) : (
                      col.header
                    )}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: skeletonRows }).map((_, i) => (
                <tr key={`sk-${i}`} className="border-b border-border last:border-0">
                  {selectable && (
                    <td className="h-12 px-4">
                      <Skeleton className="size-5 rounded" />
                    </td>
                  )}
                  {columns.map((col) => (
                    <td key={col.id} className="h-12 px-4">
                      <Skeleton className="h-4" style={{ width: `${40 + ((i + col.id.length) % 5) * 10}%` }} />
                    </td>
                  ))}
                </tr>
              ))
            ) : sortedData.length === 0 ? (
              <tr>
                <td colSpan={colSpan} className="p-0">
                  {empty ?? (
                    <div className="py-12 text-center text-xs text-muted-foreground">
                      No records found.
                    </div>
                  )}
                </td>
              </tr>
            ) : (
              sortedData.map((row) => {
                const key = keyExtractor(row)
                const isSelected = selected.has(key)
                return (
                  <tr
                    key={key}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    className={cn(
                      "border-b border-border last:border-0 transition-colors",
                      onRowClick && "cursor-pointer",
                      isSelected ? "bg-accent" : "hover:bg-muted/40"
                    )}
                  >
                    {selectable && (
                      <td
                        className="h-12 px-4"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleOne(key)}
                          aria-label="Select row"
                        />
                      </td>
                    )}
                    {columns.map((col) => (
                      <td
                        key={col.id}
                        style={{ width: col.width }}
                        className={cn(
                          "h-12 px-4 py-2 text-sm font-normal text-foreground",
                          alignClass[col.align ?? "left"],
                          col.className
                        )}
                      >
                        {col.cell(row)}
                      </td>
                    ))}
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
      {footer && (
        <div className="border-t border-border px-4 py-2.5 bg-card">{footer}</div>
      )}
    </div>
  )
}
